import { useMemo, useState } from 'react';
import { PenTool, X } from 'lucide-react';

import {
  mergeTableCellPatch,
  shouldShowTableRowHeader,
  type FormQuestion,
  type SignatureAnswerValue,
  type TableAnswerValue,
} from '@kushya/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import type { QuestionRendererProps } from '../types';
import { SignatureQuestion } from './SignatureQuestion';

type TableQuestionValue = TableAnswerValue | undefined;

const getCellValue = (
  value: TableQuestionValue,
  rowId: string,
  columnId: string,
) => value?.cells?.[rowId]?.[columnId];

const getSignatureValue = (value: unknown): SignatureAnswerValue | string | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object' && value !== null) {
    const record = value as SignatureAnswerValue;
    if (typeof record.url === 'string' && record.url.trim()) {
      return record;
    }
  }

  return null;
};

const getSignatureUrl = (value: SignatureAnswerValue | string | null) => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value.startsWith('http') ? value : null;
  }
  return value.url;
};

export const TableQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  sessionToken,
  ensureSessionToken,
  mode,
  disabled,
}: QuestionRendererProps<TableAnswerValue>) => {
  if (question.type !== 'table') {
    return null;
  }

  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [signatureCell, setSignatureCell] = useState<{
    rowId: string;
    columnId: string;
  } | null>(null);
  const isInteractive = mode === 'runtime' && !disabled;
  const showRowHeader = shouldShowTableRowHeader(question);

  const updateCell = (rowId: string, columnId: string, cellValue: unknown) => {
    if (!onChange) {
      return;
    }

    const patch: TableAnswerValue = {
      cells: {
        [rowId]: {
          [columnId]: cellValue,
        },
      },
    };

    onChange(mergeTableCellPatch(value, patch));
  };

  const activeSignatureColumn = signatureCell
    ? question.columns.find((column) => column.id === signatureCell.columnId)
    : undefined;
  const activeSignatureRow = signatureCell
    ? question.rows.find((row) => row.id === signatureCell.rowId)
    : undefined;
  const activeSignatureValue = signatureCell
    ? getSignatureValue(getCellValue(value, signatureCell.rowId, signatureCell.columnId))
    : null;

  const signatureQuestion = useMemo<FormQuestion | null>(() => {
    if (!signatureCell || !activeSignatureColumn || !activeSignatureRow) {
      return null;
    }

    return {
      id: `${question.id}_${activeSignatureRow.id}_${activeSignatureColumn.id}`,
      ref: `${question.ref}_${activeSignatureRow.id}_${activeSignatureColumn.id}`,
      title: `${activeSignatureRow.label} - ${activeSignatureColumn.label}`,
      type: 'signature',
      required: activeSignatureColumn.required ?? false,
      requireTypedName:
        activeSignatureColumn.type === 'signature'
          ? (activeSignatureColumn.requireTypedName ?? false)
          : false,
    };
  }, [activeSignatureColumn, activeSignatureRow, question.id, question.ref, signatureCell]);

  return (
    <div className="max-h-[70vh] overflow-auto rounded-xl border border-border/70 bg-card shadow-sm">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
            {showRowHeader ? (
              <th className="sticky top-0 z-20 w-44 border-b border-border/70 bg-muted px-4 py-3 font-semibold">
                Row
              </th>
            ) : null}
            {question.columns.map((column) => (
              <th
                key={column.id}
                className="sticky top-0 z-20 min-w-44 border-b border-border/70 bg-muted px-4 py-3 font-semibold"
              >
                {column.label}
                {column.required ? <span className="ml-1 text-destructive">*</span> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {question.rows.map((row) => (
            <tr key={row.id} className="border-b border-border/50 last:border-b-0">
              {showRowHeader ? (
                <th className="bg-muted/20 px-4 py-3 text-left font-medium text-foreground">
                  {row.label}
                </th>
              ) : null}
              {question.columns.map((column) => {
                const cellKey = `${row.id}:${column.id}`;
                const cellValue = getCellValue(value, row.id, column.id);

                return (
                  <td key={column.id} className="px-4 py-3 align-top">
                    {column.type === 'signature' ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-auto min-h-12 w-full justify-center gap-2 px-3 py-2"
                        disabled={!isInteractive}
                        onClick={() =>
                          setSignatureCell({ rowId: row.id, columnId: column.id })
                        }
                      >
                        {getSignatureUrl(getSignatureValue(cellValue)) ? (
                          <img
                            src={getSignatureUrl(getSignatureValue(cellValue)) ?? undefined}
                            alt={`${row.label} signature`}
                            className="h-10 max-w-36 rounded bg-white object-contain"
                          />
                        ) : (
                          <>
                            <PenTool className="size-4" />
                            Sign
                          </>
                        )}
                      </Button>
                    ) : editingCell === cellKey ? (
                      <Input
                        autoFocus
                        value={typeof cellValue === 'string' ? cellValue : ''}
                        placeholder={column.placeholder}
                        disabled={!isInteractive}
                        onChange={(event) =>
                          updateCell(
                            row.id,
                            column.id,
                            event.target.value.trim() ? event.target.value : null,
                          )
                        }
                        onBlur={() => {
                          setEditingCell(null);
                          onBlur?.();
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="min-h-11 w-full rounded-md border border-border/60 bg-background px-3 py-2 text-left text-foreground transition-colors hover:border-primary/50 disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={!isInteractive}
                        onClick={() => setEditingCell(cellKey)}
                      >
                        {typeof cellValue === 'string' && cellValue.trim() ? (
                          cellValue
                        ) : (
                          <span className="text-muted-foreground">
                            {column.placeholder || 'Enter text'}
                          </span>
                        )}
                      </button>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {signatureCell && signatureQuestion ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-border bg-card p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {signatureQuestion.title}
                </p>
                <p className="text-xs text-muted-foreground">
                  Save the signature to place it in the table cell.
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setSignatureCell(null)}
              >
                <X className="size-4" />
              </Button>
            </div>
            <SignatureQuestion
              question={signatureQuestion}
              value={activeSignatureValue}
              sessionToken={sessionToken}
              ensureSessionToken={ensureSessionToken}
              mode={mode}
              disabled={disabled}
              onChange={(signatureValue) => {
                updateCell(
                  signatureCell.rowId,
                  signatureCell.columnId,
                  signatureValue,
                );
              }}
              onBlur={() => {
                setSignatureCell(null);
                onBlur?.();
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
